import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { getXQuoteIdFromHash, trackAtlasEvent } from "./analytics";
import type { Language } from "./locale";

const LANGUAGE_PREFIX = /^\/(en|uk|ru)(?=\/|$)/;
const HISTORY_STATE_KEY = "__atlasNavigation";
export const ROUTE_FOCUS_EVENT = "atlas2:route-focus";

type AtlasNavigationState = {
  version: 1;
  chainId: string;
  index: number;
  atlasFrom: string | null;
};

type NavigateOptions = {
  replace?: boolean;
};

function currentLocation() {
  if (typeof window === "undefined") return serverLocation;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

let serverLocation = "/";

export function setServerLocation(location: string) {
  serverLocation = location.startsWith("/") ? location : "/";
}

function requestRouteFocus(location: string) {
  window.dispatchEvent(new CustomEvent(ROUTE_FOCUS_EVENT, { detail: { location } }));
}

function internalDestinationType(href: string) {
  const pathname = localizedRoute(new URL(href, window.location.origin).pathname).pathname;
  if (pathname === "/") return "introduction";
  if (pathname === "/method") return "method";
  if (pathname === "/sources") return "sources";
  if (pathname === "/library") return "library";
  if (pathname === "/authors") return "authors";
  if (/^\/part\/[^/]+\/?$/.test(pathname)) return "part";
  if (/^\/chapter\/[^/]+\/?$/.test(pathname)) return "chapter";
  if (/^\/essay\/[^/]+\/audio\/?$/.test(pathname)) return "transcript";
  if (/^\/essay\/[^/]+\/?$/.test(pathname)) return "essay";
  return "other";
}

function historyEnvelope() {
  return window.history.state && typeof window.history.state === "object"
    ? window.history.state as Record<string, unknown>
    : {};
}

function readNavigationState(): AtlasNavigationState | null {
  const candidate = historyEnvelope()[HISTORY_STATE_KEY];
  if (!candidate || typeof candidate !== "object") return null;
  const state = candidate as Partial<AtlasNavigationState>;
  if (
    state.version !== 1
    || typeof state.chainId !== "string"
    || typeof state.index !== "number"
    || !Number.isInteger(state.index)
    || (state.atlasFrom !== null && typeof state.atlasFrom !== "string")
  ) return null;
  return state as AtlasNavigationState;
}

function createChainId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function initializeNavigationHistory(): AtlasNavigationState {
  const existing = readNavigationState();
  if (existing) return existing;
  const initial: AtlasNavigationState = {
    version: 1,
    chainId: createChainId(),
    index: 0,
    atlasFrom: null,
  };
  window.history.replaceState({ ...historyEnvelope(), [HISTORY_STATE_KEY]: initial }, "", currentLocation());
  return initial;
}

function isInternalAtlasLocation(value: string | null): value is string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return false;
  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

function canReturnWithinAtlas() {
  if (typeof window === "undefined") return false;
  const state = readNavigationState();
  return Boolean(state && state.index > 0 && isInternalAtlasLocation(state.atlasFrom));
}

export function localizedRoute(pathname: string): { language: Language; pathname: string } {
  const match = pathname.match(LANGUAGE_PREFIX);
  if (!match) return { language: "en", pathname: pathname || "/" };
  const stripped = pathname.slice(match[0].length) || "/";
  return { language: match[1] as Language, pathname: stripped.startsWith("/") ? stripped : `/${stripped}` };
}

export function localizeHref(href: string, language: Language): string {
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  const parsed = new URL(href, "https://atlas.invalid");
  const route = localizedRoute(parsed.pathname);
  const pathname = route.pathname;
  const localizedPath = language === "en"
    ? pathname
    : pathname === "/"
      ? `/${language}/`
      : `/${language}${pathname}`;
  return `${localizedPath}${parsed.search}${parsed.hash}`;
}

export function usePathname() {
  const [pathname, setPathname] = useState(currentLocation);

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const location = currentLocation();
      if (event.isTrusted) requestRouteFocus(location);
      setPathname(location);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return pathname;
}

export function useAtlasHistory() {
  const [canGoBack, setCanGoBack] = useState(canReturnWithinAtlas);

  useEffect(() => {
    initializeNavigationHistory();
    const sync = () => setCanGoBack(canReturnWithinAtlas());
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const goBack = () => {
    if (!canReturnWithinAtlas()) return;
    window.dispatchEvent(new Event("atlas2:before-navigate"));
    window.history.back();
  };

  return { canGoBack, goBack };
}

export function navigate(href: string, options: NavigateOptions = {}) {
  const current = currentLocation();
  if (current === href) {
    if (!href.includes("#")) {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
    }
    return;
  }
  const state = initializeNavigationHistory();
  window.dispatchEvent(new Event("atlas2:before-navigate"));
  if (options.replace) {
    window.history.replaceState({ ...historyEnvelope(), [HISTORY_STATE_KEY]: state }, "", href);
  } else {
    const nextState: AtlasNavigationState = {
      version: 1,
      chainId: state.chainId,
      index: state.index + 1,
      atlasFrom: current,
    };
    window.history.pushState({ [HISTORY_STATE_KEY]: nextState }, "", href);
    requestRouteFocus(currentLocation());
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function Link({ href, children, className, onClick, ...rest }: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick">) {
  const currentPathname = new URL(currentLocation(), "https://atlas.invalid").pathname;
  const localized = localizeHref(href, localizedRoute(currentPathname).language);
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (/^https?:/.test(localized)) return;
    event.preventDefault();
    onClick?.();
    const quoteId = getXQuoteIdFromHash(window.location.hash);
    if (
      quoteId
      && localized.startsWith("/")
      && !localized.startsWith("//")
      && currentLocation() !== localized
    ) {
      trackAtlasEvent("next_step_click", {
        quote_id: quoteId,
        destination_type: internalDestinationType(localized),
      });
    }
    navigate(localized);
  };

  return <a href={localized} className={className} onClick={handleClick} {...rest}>{children}</a>;
}
